/**
 * UI component factories — dataViews.
 */
import { registerComponent } from '../registryCore';
import { bool, num, setState, str, syntheticEvent, } from '../helpers';
import { UI, createGroup, canvasSurface } from './shared';
/** Table */
registerComponent('table', function (props, app) {
    var _a, _b;
    var columns = (_a = props.columns) !== null && _a !== void 0 ? _a : ['Name', 'Value'];
    var rows = (_b = props.rows) !== null && _b !== void 0 ? _b : [
        ['Row A', '1'],
        ['Row B', '2'],
    ];
    var colW = num(props, 'colWidth', 100);
    var width = num(props, 'width', colW * columns.length);
    var rowH = 36;
    var showSearch = bool(props, 'showSearch', false);
    var search = str(props, 'search', str(props, 'searchQuery', ''));
    var striped = bool(props, 'striped', true);
    var searchH = showSearch ? 40 : 0;
    var q = search.trim().toLowerCase();
    var filtered = q.length === 0
        ? rows
        : rows.filter(function (row) { return row.some(function (cell) { return String(cell).toLowerCase().includes(q); }); });
    var tableH = searchH + rowH * (filtered.length + 1);
    var sortable = bool(props, 'sortable', false);
    var sortColumn = num(props, 'sortColumn', -1);
    var sortDirection = str(props, 'sortDirection', 'asc');
    var selectedRow = num(props, 'selectedRow', -1);
    var group = createGroup(app, 'table', props, { focusable: true, role: 'grid' });
    group.add(canvasSurface(app, width, tableH, { radius: UI().radius }));
    if (showSearch) {
        group.add(app.roundedRect({
            x: 8,
            y: 8,
            width: width - 16,
            height: 28,
            cornerRadius: UI().radiusSm,
            fill: UI().surfaceMuted,
            stroke: UI().border,
            strokeWidth: 1,
            listening: false,
        }));
        group.add(app.text({
            text: search || 'Search…',
            x: 16,
            y: 14,
            fontSize: UI().fontSizeSm,
            fill: search ? UI().text : UI().textMuted,
            listening: false,
        }));
    }
    group.add(app.rect({
        width: width,
        height: rowH,
        y: searchH,
        fill: UI().surfaceMuted,
        stroke: null,
        listening: false,
    }));
    columns.forEach(function (col, ci) {
        var sorted = sortable && sortColumn === ci;
        var arrow = sorted ? (sortDirection === 'asc' ? ' ▲' : ' ▼') : sortable ? ' ⇅' : '';
        group.add(app.text({
            text: col.toUpperCase() + arrow,
            x: ci * colW + 14,
            y: searchH + 10,
            fontSize: UI().fontSizeSm,
            fontWeight: 'bold',
            fill: sorted ? UI().primary : UI().textMuted,
            listening: false,
        }));
    });
    filtered.forEach(function (row, ri) {
        var rowY = searchH + (ri + 1) * rowH;
        var zebra = striped && ri % 2 === 1;
        var selected = ri === selectedRow;
        if (zebra || selected) {
            group.add(app.rect({
                width: width,
                height: rowH,
                y: rowY,
                fill: selected ? UI().primarySubtle : UI().surfaceMuted,
                opacity: selected ? 1 : 0.5,
                listening: false,
            }));
        }
        if (selected) {
            group.add(app.rect({
                width: 3,
                height: rowH,
                y: rowY,
                fill: UI().primary,
                listening: false,
            }));
        }
        var rowGroup = app.group({ x: 0, y: rowY, listening: true, metadata: { rowIndex: ri } });
        row.forEach(function (cell, ci) {
            rowGroup.add(app.text({
                text: cell,
                x: ci * colW + 14,
                y: 10,
                fontSize: UI().fontSize,
                fill: selected ? UI().primary : UI().text,
                listening: false,
            }));
        });
        rowGroup.on('click', function () {
            var _a;
            setState(group, { selectedRow: ri });
            group.emit('select', syntheticEvent('select', group, { index: ri, row: row }));
            (_a = group.getApp()) === null || _a === void 0 ? void 0 : _a.requestRender();
        });
        group.add(rowGroup);
    });
    setState(group, {
        columns: columns,
        rows: rows,
        selectedRow: selectedRow,
        colWidth: colW,
        width: width,
        sortable: sortable,
        sortColumn: sortColumn,
        sortDirection: sortDirection,
        striped: striped,
        showSearch: showSearch,
        search: search,
        searchQuery: search,
        stickyHeader: bool(props, 'stickyHeader', true),
        maxHeight: num(props, 'maxHeight', 0),
    });
    return group;
});
/** Tree View */
registerComponent('tree', function (props, app) {
    var _a, _b;
    var nodes = (_a = props.nodes) !== null && _a !== void 0 ? _a : [
        { label: 'Root', children: [{ label: 'Child A' }, { label: 'Child B' }] },
    ];
    var expanded = new Set((_b = props.expanded) !== null && _b !== void 0 ? _b : [0]);
    var selectedNode = str(props, 'selectedNode', '');
    var width = num(props, 'width', 220);
    var group = createGroup(app, 'tree', props, { focusable: true, role: 'tree' });
    var y = 4;
    nodes.forEach(function (node, i) {
        var parentKey = "p".concat(i);
        var parentSelected = selectedNode === parentKey;
        var header = app.group({ x: 0, y: y, listening: true, metadata: { treeKey: parentKey } });
        if (parentSelected) {
            header.add(app.roundedRect({
                width: width,
                height: 24,
                cornerRadius: UI().radiusSm,
                fill: UI().primarySubtle,
                listening: false,
            }));
        }
        header.add(app.text({
            text: (expanded.has(i) ? '▾  ' : '▸  ') + node.label,
            fontSize: UI().fontSize,
            fontWeight: '600',
            fill: parentSelected ? UI().primary : UI().text,
            x: 8,
            y: 4,
            listening: false,
        }));
        header.on('click', function () {
            var _a;
            if (expanded.has(i))
                expanded.delete(i);
            else
                expanded.add(i);
            setState(group, { expanded: Array.from(expanded), selectedNode: parentKey });
            group.emit('change', syntheticEvent('change', group, { value: i }));
            (_a = group.getApp()) === null || _a === void 0 ? void 0 : _a.requestRender();
        });
        group.add(header);
        y += 26;
        if (expanded.has(i) && node.children) {
            group.add(app.rect({
                x: 10,
                y: y,
                width: 1,
                height: node.children.length * 22,
                fill: UI().border,
                listening: false,
            }));
            node.children.forEach(function (child, ci) {
                var key = "".concat(parentKey, ".c").concat(ci);
                var leafSelected = selectedNode === key;
                var leaf = app.group({ x: 18, y: y, listening: true, metadata: { treeKey: key } });
                if (leafSelected) {
                    leaf.add(app.roundedRect({
                        width: width - 22,
                        height: 22,
                        cornerRadius: UI().radiusSm,
                        fill: UI().primarySubtle,
                        listening: false,
                    }));
                    leaf.add(app.rect({
                        width: 3,
                        height: 22,
                        fill: UI().primary,
                        listening: false,
                    }));
                }
                leaf.add(app.text({
                    text: child.label,
                    x: leafSelected ? 10 : 8,
                    y: 4,
                    fontSize: UI().fontSize,
                    fill: leafSelected ? UI().primary : UI().textSecondary,
                    listening: false,
                }));
                leaf.on('click', function (e) {
                    var _a, _b;
                    (_a = e.stopPropagation) === null || _a === void 0 ? void 0 : _a.call(e);
                    setState(group, { selectedNode: key });
                    group.emit('select', syntheticEvent('select', group, { item: child.label, value: key }));
                    (_b = group.getApp()) === null || _b === void 0 ? void 0 : _b.requestRender();
                });
                group.add(leaf);
                y += 22;
            });
        }
    });
    setState(group, { nodes: nodes, expanded: Array.from(expanded), selectedNode: selectedNode, width: width });
    return group;
});
